import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Compass,
  Menu,
  Shield,
  X,
} from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/hooks/useAuth";
import { VeeraLogo } from "@/components/VeeraLogo";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const leaderSignupPath = "/leader/signup";
const coachSignupPath = "/coach/signup";
const signInPath = "/auth?mode=login";

const leaderBullets = [
  "Create your own leadership memory",
  "Save moments you want to learn from",
  "Use Coach Veera to preserve selected meetings",
  "Reflect across days, weeks, and months",
  "Observe behaviors and patterns over time",
];

const coachBullets = [
  "Create continuity between coaching sessions",
  "Bring your coaching philosophy into the work",
  "Carry your coaching language between sessions",
  "Keep track of real client moments over time",
  "See whether your coaching is landing in practice",
  "Reflect on how you are showing up as a coach",
  "Keep human judgment where it belongs",
];

const trustCards = [
  "No scoring",
  "No surveillance",
  "Leader-owned context",
  "Human coaching stays central",
];

const faqs = [
  {
    value: "what",
    question: "What is Coach Veera?",
    answer:
      "Coach Veera is a continuity platform for leadership growth and coaching. Leaders use it to build memory across real leadership moments. Coaches use it to carry their coaching philosophy, language, and client context between sessions.",
  },
  {
    value: "which-path",
    question: "Which path should I choose?",
    answer:
      "Choose the leader path if you want continuity in your own leadership. Choose the coach path if you want continuity in your coaching practice and with the clients you support.",
  },
  {
    value: "invite-link",
    question: "What if my coach invited me?",
    answer:
      "Use the invite link your coach sent. That link takes you into the invited-leader flow automatically, so you do not need to choose a public path here.",
  },
  {
    value: "without-coach",
    question: "Can I use Coach Veera without a coach?",
    answer:
      "Yes. You can start as a leader, save selected moments, choose a leadership lens, and build a fuller picture of how you show up over time. If you decide to work with a coach later, you can bring that record into the conversation.",
  },
  {
    value: "coaches",
    question: "How do coaches use Coach Veera?",
    answer:
      "Coaches use Coach Veera to preserve between-session context, carry their coaching philosophy into the leader's reflection experience, and see whether their coaching is actually showing up in the moments clients bring back.",
  },
  {
    value: "moments",
    question: "What are leadership moments?",
    answer:
      "Leadership moments are real situations where how you communicate, decide, listen, respond, or follow through matters. Coach Veera helps you keep those moments from disappearing.",
  },
  {
    value: "lens",
    question: "What is a leadership lens?",
    answer:
      "A leadership lens is a way of looking at a moment. It can come from a coaching philosophy, a leadership framework, or the questions you and your coach want to keep returning to.",
  },
  {
    value: "memory",
    question: "What does Coach Veera remember?",
    answer:
      "Coach Veera remembers only the moments, reflections, and selected meeting context you choose to save. Over time, that creates continuity without turning growth into a grade, label, or judgment.",
  },
  {
    value: "uploads",
    question: "What happens to selected meetings?",
    answer:
      "Selected meetings can become continuity context for reflection or coaching. Coach Veera only joins meetings you choose to bring into the product, and the value is the saved moment, chosen lens, reflection, and continuity note.",
  },
  {
    value: "bot",
    question: "What is Coach Vera Leadership Bot?",
    answer:
      "Coach Vera Leadership Bot is the meeting assistant Coach Veera can join only when a leader or coach chooses to bring it into a selected meeting. It is not always on, does not score people, and does not replace a coach. Its job is to preserve useful context so reflection has a real moment to return to.",
  },
  {
    value: "replace",
    question: "Does Coach Veera replace a coach?",
    answer:
      "No. Coach Veera preserves context so leaders and coaches can reflect with more clarity. It does not score leaders, label moments, or replace human judgment.",
  },
  {
    value: "total-focus",
    question: "What is Total Focus?",
    answer:
      "Total Focus is Coach Veera's coaching partner based in Australia. They support leaders who are looking for coaches across the world and may want a human coaching partner alongside Coach Veera.",
  },
];

function AudienceCard({
  eyebrow,
  headline,
  body,
  bullets,
  cta,
  onClick,
  testId,
}: {
  eyebrow: string;
  headline: string;
  body: string;
  bullets: string[];
  cta: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Card data-testid={testId} className="h-full min-w-0 border-primary/10 bg-card/95 shadow-sm">
      <CardContent className="flex h-full flex-col gap-6 p-7 lg:p-8">
        <div className="space-y-3">
          <h3 className="text-small font-semibold uppercase tracking-[0.18em] text-primary">
            {eyebrow}
          </h3>
          <p className="text-h2 text-foreground">{headline}</p>
          <p className="text-body leading-7 text-muted-foreground">{body}</p>
        </div>
        <ul className="space-y-3">
          {bullets.map((item) => (
            <li key={item} className="flex gap-3 text-body text-foreground">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <Button className="mt-auto min-h-12 w-full whitespace-normal text-center sm:w-fit" size="lg" onClick={onClick}>
          {cta}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </CardContent>
    </Card>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const { user, roles, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isCoach = roles.includes("coach");
  const isLeader = roles.includes("leader");
  const isAuthenticated = !!user && !loading;

  if (!loading && user) {
    if (isCoach) return <Navigate to="/coach/overview" replace />;
    if (isLeader) return <Navigate to="/today" replace />;
    return <Navigate to="/post-auth" replace />;
  }

  const navItems = [
    { label: "FAQ", action: () => scrollTo("faq") },
    { label: "Pricing", action: () => navigate("/pricing") },
  ];

  return (
    <>
      <Helmet>
        <title>Coach Veera | Coaching Continuity</title>
        <meta
          name="description"
          content="Coach Veera helps leaders and coaches build continuity from selected meetings, reflections, and leadership moments."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {isAuthenticated && (
          <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-5 py-3">
              <div className="flex items-center justify-between">
                <VeeraLogo size="lg" />
                <div className="flex flex-col items-end gap-2">
                  <span className="text-caption text-muted-foreground">Signed in</span>
                  <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
                    {isCoach && (
                      <Button size="sm" onClick={() => navigate("/coach/overview")}>
                        Coach home
                      </Button>
                    )}
                    {isLeader && (
                      <Button size="sm" variant="outline" onClick={() => navigate("/today")}>
                        Leader home
                      </Button>
                    )}
                    {!isCoach && !isLeader && (
                      <Button size="sm" onClick={() => navigate("/auth")}>
                        Get started
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        {!isAuthenticated && (
          <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                <VeeraLogo size="lg" />

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
                  {navItems.map((item) => (
                    <Button
                      key={item.label}
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={item.action}
                    >
                      {item.label}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => navigate(signInPath)}
                  >
                    Sign In
                  </Button>
                  <Button size="sm" onClick={() => navigate(leaderSignupPath)}>
                    Start Free
                  </Button>
                </nav>

                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </div>

              {mobileMenuOpen && (
                <div data-testid="mobile-menu" className="space-y-2 border-t border-border pt-3 pb-2 lg:hidden">
                  {navItems.map((item) => (
                    <Button
                      key={item.label}
                      variant="ghost"
                      className="w-full justify-start text-muted-foreground"
                      onClick={() => {
                        item.action();
                        setMobileMenuOpen(false);
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-muted-foreground"
                    onClick={() => {
                      navigate(signInPath);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="w-full"
                    onClick={() => {
                      navigate(leaderSignupPath);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Start Free
                  </Button>
                </div>
              )}
            </div>
          </header>
        )}

        <main>
          <section className="relative overflow-hidden border-b border-violet-950/20 bg-[radial-gradient(circle_at_78%_18%,rgba(181,113,63,0.22),transparent_28%),radial-gradient(circle_at_68%_72%,rgba(55,177,162,0.16),transparent_34%),linear-gradient(135deg,#0e0a21_0%,#1a0f34_52%,#2a174d_100%)] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -right-32 top-8 h-[34rem] w-[34rem] rounded-full border border-[#b5713f]/15" />
              <div className="absolute -right-16 top-24 h-[25rem] w-[25rem] rounded-full border border-[#b5713f]/10" />
              <div className="absolute bottom-0 left-1/3 h-px w-2/3 bg-gradient-to-r from-transparent via-[#37b1a2]/40 to-transparent" />
            </div>

            <div className="relative container mx-auto px-5 py-16 md:py-20 lg:py-24">
              <div className="max-w-3xl space-y-7">
                <div className="space-y-5">
                  <h1 className="font-serif text-[3.15rem] leading-[0.95] text-white sm:text-[4.45rem] lg:text-[4.875rem]">
                    Continuous coaching, customized to the leader.
                  </h1>
                  <p className="max-w-2xl text-h3 font-normal leading-8 text-violet-100/88">
                    Coach Veera helps leaders and coaches carry continuity and growth between
                    moments that matter.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    className="group min-h-[9.5rem] rounded-lg border border-[#fff8ef]/28 bg-[#fff8ef] p-5 text-left text-violet-950 shadow-[0_18px_45px_rgba(0,0,0,0.24)] transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#77dbd0] focus:ring-offset-2 focus:ring-offset-[#151124]"
                    onClick={() => navigate(leaderSignupPath)}
                  >
                    <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-violet-950/10 text-violet-950">
                      <Compass className="h-5 w-5" />
                    </span>
                    <span className="block text-h3 font-semibold">I'm a leader</span>
                    <span className="mt-2 block text-body leading-6 text-violet-950/75">
                      I want continuity and growth in my leadership.
                    </span>
                    <span className="mt-4 inline-flex items-center gap-2 text-small font-semibold text-violet-950">
                      Start my path
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="group min-h-[9.5rem] rounded-lg border border-[#d99b67]/65 bg-white/5 p-5 text-left text-white transition hover:bg-[#fff8ef] hover:text-violet-950 focus:outline-none focus:ring-2 focus:ring-[#77dbd0] focus:ring-offset-2 focus:ring-offset-[#151124]"
                    onClick={() => navigate(coachSignupPath)}
                  >
                    <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-white/10 text-[#f0b37b] transition group-hover:bg-violet-950/10 group-hover:text-violet-950">
                      <Briefcase className="h-5 w-5" />
                    </span>
                    <span className="block text-h3 font-semibold">I'm a coach</span>
                    <span className="mt-2 block text-body leading-6 text-violet-100/78 transition group-hover:text-violet-950/75">
                      I want continuity in my coaching practice.
                    </span>
                    <span className="mt-4 inline-flex items-center gap-2 text-small font-semibold">
                      Start my coach path
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </span>
                  </button>
                </div>

                <div className="flex flex-col gap-4 text-small text-violet-100/85">
                  <div className="flex flex-wrap gap-2">
                    {["No scoring", "No surveillance", "Human coaching stays central"].map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-white/22 bg-white/8 px-3 py-1.5 font-semibold text-white backdrop-blur"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 lg:py-24">
            <div className="container mx-auto px-5">
              <div className="mx-auto mb-10 max-w-3xl text-center">
                <h2 className="text-h1 text-foreground">Choose the path that fits where you are.</h2>
                <p className="mt-4 text-body leading-7 text-muted-foreground">
                  Coach Veera supports leader-led reflection and coach-led continuity by helping
                  selected moments become a record human coaching can actually use.
                </p>
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <AudienceCard
                  testId="for-leaders-section"
                  eyebrow="For Leaders"
                  headline="Build continuity in your leadership."
                  body="Create your own leadership memory across the moments that matter. Over time, Coach Veera helps you see where you show up well, where you can grow, and what patterns are worth bringing back to reflection or coaching."
                  bullets={leaderBullets}
                  cta="Start as a leader"
                  onClick={() => navigate(leaderSignupPath)}
                />
                <AudienceCard
                  testId="for-coaches-section"
                  eyebrow="For Coaches"
                  headline="Build continuity in your coaching practice."
                  body="Bring your coaching philosophy and language into the space between sessions. Coach Veera helps preserve real client moments over time, and helps you reflect on whether your coaching is actually landing in practice."
                  bullets={coachBullets}
                  cta="Start as a coach"
                  onClick={() => navigate(coachSignupPath)}
                />
              </div>
            </div>
          </section>

          <section className="py-20 lg:py-24">
            <div className="container mx-auto px-5">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-h1 text-foreground">
                  Built for trust-sensitive leadership work.
                </h2>
                <p className="mt-4 text-body leading-7 text-muted-foreground">
                  Coach Veera does not score, surveil, label, or replace human coaching. It only joins
                  selected meetings, and its job is to preserve real moments so leaders and coaches
                  can reflect with more context.
                </p>
              </div>
              <div className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {trustCards.map((item) => (
                  <div key={item} className="rounded-2xl border border-primary/10 bg-primary/5 p-5 text-center">
                    <Shield className="mx-auto mb-3 h-6 w-6 text-primary" />
                    <p className="text-body font-semibold text-foreground">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="faq" data-testid="faq-section" className="bg-muted/30 py-20 lg:py-24">
            <div className="container mx-auto px-5">
              <div className="mx-auto mb-10 max-w-2xl text-center">
                <h2 className="text-h1 text-foreground">FAQ</h2>
              </div>
              <div className="mx-auto max-w-3xl">
                <Accordion type="single" collapsible className="w-full rounded-2xl border border-border bg-card px-5">
                  {faqs.map((item) => (
                    <AccordionItem key={item.value} value={item.value}>
                      <AccordionTrigger>{item.question}</AccordionTrigger>
                      <AccordionContent>{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>

        </main>

        <footer className="border-t border-border py-8">
          <div className="container mx-auto px-5">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <VeeraLogo size="lg" />
              <div className="flex flex-wrap items-center justify-center gap-4 text-caption">
                <button
                  onClick={() => navigate(signInPath)}
                  className="font-medium text-primary hover:text-primary/80"
                >
                  Sign In
                </button>
                <a href="/privacy" className="text-muted-foreground hover:text-foreground">
                  Privacy Policy
                </a>
                <a href="/terms" className="text-muted-foreground hover:text-foreground">
                  Terms of Service
                </a>
                <a href="/trust" className="text-muted-foreground hover:text-foreground">
                  Trust & Security
                </a>
                <a href="/pricing" className="text-muted-foreground hover:text-foreground">
                  Pricing
                </a>
                <a href="/support" className="text-muted-foreground hover:text-foreground">
                  Contact
                </a>
              </div>
              <p className="text-caption text-muted-foreground">
                © {new Date().getFullYear()} Coach Veera. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
